import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconAtom2Fill18 } from "nucleo-ui-fill-18"
import {
	IconCircleCheckOutline18 as CircleCheck,
	IconCircleXmarkOutline18 as CircleXmark,
	IconGlobeOutline18 as Globe,
	IconPlusOutline18 as Plus,
	IconXmarkOutline18 as Xmark,
} from "nucleo-ui-outline-18"

import {
	publicCopyFor,
	resolveCardTarget,
} from "@productlane/lib/src/feature-request-card"
import { portalName } from "@productlane/lib/src/portal-visibility"
import { Button, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { TimelineEventProps } from "./types"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { usePortalEntries } from "@/lib/hooks/use-portal-entries"
import { useVisibilityActions } from "@/lib/hooks/useVisibilityActions"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { useThreadContext } from "../../../thread-context"
import { AgentMessageAvatar } from "./base/agent-message-avatar"
import { SlackStyleMessage } from "./base/slack-style-message"
import {
	FEATURE_REQUEST_CARD_EVENT_TYPE,
	parseFeatureRequestCardPayload,
} from "./feature-request-card"
import { AI_CHAT_ACTOR_NAME } from "./improve-reply"

type Kind = "issue" | "project"

export function FeatureRequestCardEvent({ event }: TimelineEventProps) {
	const { thread } = useThreadContext()
	const { role } = useRole()
	const zero = useZero()
	const { entries, namedPortalIds } = usePortalEntries()
	const { setPortalVisibility } = useVisibilityActions()
	const [projects] = useQuery(queries.projects())
	const [busy, setBusy] = useState<null | "publish" | "create" | "dismiss">(
		null,
	)

	const dismissMutation = api.featureRequestCards.dismiss.useMutation()
	const publishedMutation = api.featureRequestCards.markPublished.useMutation()
	const createMutation = api.featureRequestCards.createAndPublish.useMutation()

	const rawPayload =
		event.type === "activity" &&
		event.threadActivity.eventType === FEATURE_REQUEST_CARD_EVENT_TYPE
			? event.threadActivity.payload
			: null
	const payload = useMemo(
		() => (rawPayload ? parseFeatureRequestCardPayload(rawPayload) : null),
		[rawPayload],
	)

	const matchId = payload?.match?.id ?? ""
	const [matchedIssue] = useQuery(queries.issueById(matchId), {
		enabled: matchId !== "",
	})

	const [kind, setKind] = useState<Kind | null>(null)

	const target = useMemo(() => {
		if (!payload) return null
		const linkedProjectIds = new Set(thread.projects.map((p) => p.id))
		const matched = payload.match ? (matchedIssue ?? null) : null
		const candidate = (row: {
			id: string
			isVisible: boolean | null
			portalInstanceIds: readonly string[] | null
			publicTitle: string | null
			publicDescription: string | null
			identifier?: string | null
		}) => ({
			id: row.id,
			isVisible: row.isVisible,
			portalInstanceIds: row.portalInstanceIds,
			publicTitle: row.publicTitle,
			publicDescription: row.publicDescription,
			identifier: row.identifier ?? null,
		})
		return resolveCardTarget({
			portalId: payload.portalId,
			linkedIssues: thread.issues.filter((i) => !i.isDeleted).map(candidate),
			linkedProjects: projects
				.filter((p) => linkedProjectIds.has(p.id))
				.map(candidate),
			matchedIssue: matched ? candidate(matched) : null,
		})
	}, [payload, thread.issues, thread.projects, matchedIssue, projects])

	if (event.type !== "activity" || !payload || !target) return null

	const portal = portalName(payload.portalId, entries)
	const canAct = role !== undefined && role !== "VIEWER"
	const activityId = event.threadActivity.id
	const createKind: Kind = kind ?? payload.kind

	const item = target.kind === "create" ? null : target.item
	const copy = item ? publicCopyFor(item, payload) : null
	const title = copy?.publicTitle ?? payload.title
	const description = copy?.publicDescription ?? payload.description
	const itemLabel = !item
		? createKind === "project"
			? "New project"
			: "New issue"
		: item.identifier
			? target.kind === "match"
				? `Matches ${item.identifier}`
				: `Linked to ${item.identifier}`
			: target.kind === "match"
				? "Matches a project"
				: "Linked project"

	const fail = (error: unknown, action: string, message: string) => {
		captureError(error, {
			domain: "portal",
			action,
			workspaceId: thread.workspaceId,
			extra: { activityId },
		})
		toast.error({
			title: message,
			description: error instanceof Error ? error.message : undefined,
			icon: IconAtom2Fill18,
		})
	}

	const publishExisting = async () => {
		if (!item || target.kind === "create") return
		setBusy("publish")
		try {
			if (target.kind === "match") {
				await mutateOk(
					zero.mutate(
						mutators.issues.linkExisting({
							threadId: thread.id,
							issueId: item.id,
							customerNeed: {
								body: thread.text ?? "",
								priority: Number(thread.painLevel === "HIGH"),
								videoId: thread.videoId,
							},
						}),
					),
				)
			}
			await setPortalVisibility({
				id: item.id,
				type: target.type,
				portalId: payload.portalId,
				value: true,
				namedPortalIds,
				current: item,
			})
			const publicCopy = publicCopyFor(item, payload)
			if (target.type === "issue") {
				await mutateOk(
					zero.mutate(mutators.issues.update({ id: item.id, ...publicCopy })),
				)
			} else {
				await mutateOk(
					zero.mutate(mutators.projects.update({ id: item.id, ...publicCopy })),
				)
			}
			await publishedMutation.mutateAsync({
				activityId,
				item: { type: target.type, id: item.id },
			})
			toast.success({ title: `"${title}" is now visible on ${portal}` })
		} catch (error) {
			fail(error, "publish feature request", "Couldn't publish the request")
		} finally {
			setBusy(null)
		}
	}

	const createAndPublish = async () => {
		setBusy("create")
		try {
			await createMutation.mutateAsync({ activityId, kind: createKind })
			toast.success({ title: `"${title}" is now visible on ${portal}` })
		} catch (error) {
			fail(error, "create feature request", `Couldn't create the ${createKind}`)
		} finally {
			setBusy(null)
		}
	}

	const dismiss = async () => {
		setBusy("dismiss")
		try {
			await dismissMutation.mutateAsync({ activityId })
		} catch (error) {
			fail(error, "dismiss feature request", "Couldn't dismiss the card")
		} finally {
			setBusy(null)
		}
	}

	const decidedLine =
		payload.status === "published" ||
		(target.kind === "linked" && target.shown) ? (
			<>
				<CircleCheck className="h-3.5 w-3.5 flex-none" />
				<span>Visible on {portal}</span>
			</>
		) : payload.status === "dismissed" ? (
			<>
				<CircleXmark className="h-3.5 w-3.5 flex-none" />
				<span>Dismissed</span>
			</>
		) : null

	const footer = decidedLine ? (
		<div className="flex h-7 items-center gap-1.5 text-xs text-tertiary">
			{decidedLine}
		</div>
	) : canAct ? (
		<div className="flex h-7 items-center gap-2">
			{target.kind === "create" ? (
				<>
					<Button
						size="sm"
						Icon={Plus}
						isLoading={busy === "create"}
						disabled={busy !== null}
						onClick={() => void createAndPublish()}
					>
						{createKind === "project"
							? "Create project and publish"
							: "Create issue and publish"}
					</Button>
					<Button
						size="sm"
						variant="outline"
						disabled={busy !== null}
						onClick={() =>
							setKind(createKind === "project" ? "issue" : "project")
						}
					>
						{createKind === "project"
							? "Make it an issue"
							: "Make it a project"}
					</Button>
				</>
			) : (
				<Button
					size="sm"
					Icon={Globe}
					isLoading={busy === "publish"}
					disabled={busy !== null}
					onClick={() => void publishExisting()}
				>
					Publish on {portal}
				</Button>
			)}
			<Button
				size="sm"
				variant="outline"
				Icon={Xmark}
				isLoading={busy === "dismiss"}
				disabled={busy !== null}
				onClick={() => void dismiss()}
			>
				Dismiss
			</Button>
		</div>
	) : null

	return (
		<div className="py-1" data-feature-request-card={payload.status}>
			<div className="-mx-2 rounded-2xl border border-dashed border-blue-300 bg-blue-50/60 px-[15px] pb-[9px] pt-[11px] dark:border-blue-400/30 dark:bg-blue-500/5">
				<SlackStyleMessage
					showBackground={false}
					senderName={AI_CHAT_ACTOR_NAME}
					timestamp={new Date(event.threadActivity.createdAt)}
					isOutbound
					shrinkActions
					actions={
						<Tooltip
							label={`Customers on ${portal} see this only after you publish it.`}
						>
							<span className="block min-w-0 truncate text-xs text-tertiary">
								Feature request · Team only
							</span>
						</Tooltip>
					}
					avatarContent={<AgentMessageAvatar />}
					footer={footer}
				>
					<div className="flex flex-col gap-1">
						<div className="text-sm font-medium text-primary">{title}</div>
						<div className="text-sm text-secondary">{description}</div>
						<div className="text-xs text-tertiary">{itemLabel}</div>
					</div>
				</SlackStyleMessage>
			</div>
		</div>
	)
}
